/**
 * Represents the response after importing a calibration lot (CalLot).
 * It includes the status of the import and the calibration lot data.
 */
export class CalLotImportResponse {
  status!: string;
  calLot!: CalLot;
}
/**
 * Represents the details of a calibration lot.
 * It includes information such as kit name, method ID, assay panel name, 
 * calibration lot number, reagent lot, storage temperature, and assays.
 */
export class CalLot {
  kitName: string = '';
  methodId!: number;
  assayPanelName: string = '';
  calibratorCatalogNumber: string = '';
  aPFName: string = '';
  calLotNumber: string = '';
  calLotExpirationDate: string = '';
  valueAssignmentVersion!: number;
  reagentLot: string = '';
  recommendedStorageTemp: string = '';
  fillVolume!: number;
  vialCount!: number;
  assays: CalAssay[] = [];
  vials: CalVial[] = [];
}
/**
 * Represents an assay used in a calibration lot.
 * It includes the region, assay name, units, decimal precision, mean bias, and slope.
 */
export class CalAssay {
  region!: number;
  assayName: string = '';
  units: string = '';
  decimalPrecision!: number;
  meanBias!: number;
  slope!: number;
}
/**
 * Represents a vial in the calibration lot.
 * It includes the vial's barcode, level lot number, levels, and vial number.
 */
export class CalVial {
  barcode: string = '';
  levelLotNumber: string = '';
  levels: CalVialLevel[] = [];
  vialNumber!: number;
}
/**
 * Represents a level within a vial in the calibration lot.
 * It includes the region and the concentration for that level.
 */
export class CalVialLevel {
  region!: number;
  concentration!: number;
}
/** Key-value object for storing cal lot data during initialization. */
export type CalLotData = Record<string, string | number>;
/**
 * Represents metadata related to the calibration lot file.
 * It includes the panel name, calibration lot number, revision, and source file.
 */
export class CalLotFile {
  panelName: string = '';
  calLotNumber: string = '';
  revision: string = '';
  sourceFile: string = '';
}
/** Map of assays for each cal lot number that have vial concentration (visible columns). */
export class AssaysWithConcentrationByLot {
  [calLotNumber: string]: CalAssay[];
}
/**
 * Represents the mean bias and slope for each calibration lot, 
 * where the key is the calibration lot number and the value is an object containing the mean bias and slope.
 */
export class MeanBiasAndSlope {
  [key: string]: number; // Dynamic key
}
